import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  InputAdornment,
  Select,
  FormControl,
  Button,
  SelectChangeEvent,
  SxProps,
  Theme,
} from "@mui/material";
import {
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import "./TableView.css";

interface FilterOption {
  value: string;
  label: string;
}

interface Column {
  id: string;
  label: string;
  render?: (value: unknown, row: RowData) => ReactNode;
  hidden?: boolean;
  sortable?: boolean;
  sortValue?: (value: unknown, row: RowData) => string | number;
  filterable?: boolean;
  filterType?: "text" | "select";
  filterOptions?: (FilterOption | string)[];
  filterValue?: (value: unknown, row: RowData) => string;
  filterFunction?: (
    cellValue: string,
    filterValue: string,
    row: RowData,
  ) => boolean;
}

interface RowData {
  id: string | number;
  [key: string]: unknown;
}

interface RowProps {
  sx?: SxProps<Theme>;
  [key: string]: unknown;
}

interface TableViewProps {
  columns: Column[];
  data: RowData[];
  loading?: boolean;
  onRowClick?: (row: RowData) => void;
  searchable?: boolean;
  filterable?: boolean;
  selectable?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  getRowProps?: (row: RowData) => RowProps;
  columnFilterable?: boolean;
  onColumnFilterChange?: (filters: Record<string, string>) => void;
  defaultColumnFilters?: Record<string, string>;
}

const TableView: React.FC<TableViewProps> = ({
  columns,
  data,
  loading = false,
  onRowClick,
  searchable = true,
  selectable = false,
  onSelectionChange,
  getRowProps,
  columnFilterable = true,
  onColumnFilterChange,
  defaultColumnFilters = {},
}) => {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(
    columns.reduce(
      (acc, col) => {
        acc[col.id] = col.hidden !== true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set(),
  );
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  // Column filter state management
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      const initial: Record<string, string> = {};
      columns.forEach((col) => {
        if (defaultColumnFilters[col.id]) {
          initial[col.id] = defaultColumnFilters[col.id];
        } else {
          initial[col.id] = "";
        }
      });
      return initial;
    },
  );

  // Update column filters when columns change
  useEffect(() => {
    setColumnFilters((prev) => {
      const updated = { ...prev };
      columns.forEach((col) => {
        if (!(col.id in updated)) {
          updated[col.id] = defaultColumnFilters[col.id] || "";
        }
      });
      return updated;
    });
  }, [columns, defaultColumnFilters]);

  // Track which columns have active filters
  const activeFilters = useMemo(() => {
    return Object.keys(columnFilters).filter((colId) => {
      const filterValue = columnFilters[colId];
      return filterValue && filterValue.toString().trim() !== "";
    });
  }, [columnFilters]);

  // Filter visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => columnVisibility[col.id] !== false);
  }, [columns, columnVisibility]);

  // Get raw cell value for filtering
  const getRawCellValue = useCallback(
    (column: Column, row: RowData): string => {
      const value = row[column.id];
      if (value === null || value === undefined) {
        return "";
      }

      // For filtering, we want the raw value, not the rendered value
      // But if column has a filterValue function, use that
      if (column.filterValue) {
        return column.filterValue(value, row);
      }

      return String(value);
    },
    [],
  );

  // Filter data based on search query and column filters
  const filteredData = useMemo(() => {
    let result = data;

    // Apply global search query first
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) => {
        return visibleColumns.some((col) => {
          const searchableValue = getRawCellValue(col, row).toLowerCase();
          return searchableValue.includes(query);
        });
      });
    }

    // Apply column filters
    if (columnFilterable && activeFilters.length > 0) {
      result = result.filter((row) => {
        return activeFilters.every((colId) => {
          const column = columns.find((col) => col.id === colId);
          if (!column) return true;

          // Check if column is filterable
          if (column.filterable === false) return true;

          const filterValue = columnFilters[colId];
          if (!filterValue || filterValue.toString().trim() === "") return true;

          // Get the cell value
          const cellValue = getRawCellValue(column, row);

          // Apply custom filter function if provided
          if (column.filterFunction) {
            return column.filterFunction(cellValue, filterValue, row);
          }

          // Default filter logic based on filter type
          const filterType = column.filterType || "text";

          if (filterType === "select") {
            // Exact match for select filters
            return (
              cellValue.toString().toLowerCase() ===
              filterValue.toString().toLowerCase()
            );
          } else {
            // Case-insensitive text matching
            return cellValue
              .toString()
              .toLowerCase()
              .includes(filterValue.toString().toLowerCase());
          }
        });
      });
    }

    return result;
  }, [
    data,
    searchQuery,
    visibleColumns,
    columnFilters,
    activeFilters,
    columnFilterable,
    columns,
    getRawCellValue,
  ]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    const column = columns.find((col) => col.id === sortColumn);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (column.sortValue) {
        aVal = column.sortValue(a[column.id], a);
        bVal = column.sortValue(b[column.id], b);
      } else {
        aVal = getRawCellValue(column, a);
        bVal = getRawCellValue(column, b);
      }

      // Numeric comparison if both values are numbers
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (
        !Number.isNaN(aNum) &&
        !Number.isNaN(bNum) &&
        aVal !== "" &&
        bVal !== ""
      ) {
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const cmp = aStr.localeCompare(bStr);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortColumn, sortDirection, columns, getRawCellValue]);

  // Handle column sort toggle
  const handleSortToggle = useCallback((columnId: string) => {
    setSortColumn((prev) => {
      if (prev === columnId) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return columnId;
      }
      setSortDirection("asc");
      return columnId;
    });
  }, []);

  // Handle column visibility toggle
  const handleColumnVisibilityToggle = useCallback((columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  }, []);

  // Handle column filter change
  const handleColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumnFilters((prev) => {
        const updated = { ...prev, [columnId]: value };
        if (onColumnFilterChange) {
          onColumnFilterChange(updated);
        }
        return updated;
      });
    },
    [onColumnFilterChange],
  );

  // Clear all column filters
  const handleClearAllFilters = useCallback(() => {
    const cleared: Record<string, string> = {};
    columns.forEach((col) => {
      cleared[col.id] = "";
    });
    setColumnFilters(cleared);
    if (onColumnFilterChange) {
      onColumnFilterChange(cleared);
    }
  }, [columns, onColumnFilterChange]);

  // Handle select all
  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = new Set<string | number>(
          sortedData.map((row) => row.id),
        );
        setSelectedRows(newSelected);
        if (onSelectionChange) {
          onSelectionChange(Array.from(newSelected));
        }
      } else {
        setSelectedRows(new Set());
        if (onSelectionChange) {
          onSelectionChange([]);
        }
      }
    },
    [sortedData, onSelectionChange],
  );

  // Handle row selection
  const handleRowSelect = useCallback(
    (rowId: string | number, event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const newSelected = new Set(selectedRows);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      setSelectedRows(newSelected);
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelected));
      }
    },
    [selectedRows, onSelectionChange],
  );

  // Handle row click
  const handleRowClickInternal = useCallback(
    (row: RowData) => {
      if (onRowClick) {
        onRowClick(row);
      }
    },
    [onRowClick],
  );

  // Check if all rows are selected
  const isAllSelected =
    sortedData.length > 0 && selectedRows.size === sortedData.length;
  const isIndeterminate =
    selectedRows.size > 0 && selectedRows.size < sortedData.length;

  // Render cell value
  const renderCellValue = useCallback(
    (column: Column, row: RowData): ReactNode => {
      const value = row[column.id];
      if (column.render) {
        return column.render(value, row);
      }
      return value != null ? String(value) : "-";
    },
    [],
  );

  // Render column filter input
  const renderColumnFilter = useCallback(
    (column: Column): ReactNode => {
      if (!columnFilterable || column.filterable === false) {
        return null;
      }

      const filterValue = columnFilters[column.id] || "";
      const hasActiveFilter = activeFilters.includes(column.id);
      const filterType = column.filterType || "text";

      if (filterType === "select" && column.filterOptions) {
        return (
          <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
            <Select
              value={filterValue}
              onChange={(e: SelectChangeEvent) =>
                handleColumnFilterChange(column.id, e.target.value)
              }
              displayEmpty
              sx={{
                fontSize: "0.75rem",
                height: "28px",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem value="">
                <em>{t("common:table.all")}</em>
              </MenuItem>
              {column.filterOptions.map((option) => {
                const optionValue =
                  typeof option === "string" ? option : option.value;
                const optionLabel =
                  typeof option === "string" ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      }

      // Text input filter
      return (
        <TextField
          size="small"
          placeholder={t("common:table.filter")}
          value={filterValue}
          onChange={(e) => handleColumnFilterChange(column.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          sx={{
            mt: 0.5,
            "& .MuiInputBase-root": {
              height: "28px",
              fontSize: "0.75rem",
            },
            "& .MuiInputBase-input": {
              padding: "4px 8px",
            },
          }}
          InputProps={{
            endAdornment: hasActiveFilter ? (
              <InputAdornment position="end">
                <FilterListIcon
                  fontSize="small"
                  color="primary"
                  sx={{ fontSize: "14px" }}
                />
              </InputAdornment>
            ) : null,
          }}
        />
      );
    },
    [columnFilterable, columnFilters, activeFilters, handleColumnFilterChange],
  );

  // Desktop table view
  const renderDesktopView = () => (
    <TableContainer component={Paper} className="table-view-container">
      <Table stickyHeader>
        <TableHead>
          {/* Column labels row */}
          <TableRow>
            {selectable && (
              <TableCell
                padding="checkbox"
                className="table-header-cell"
                rowSpan={columnFilterable ? 2 : 1}
              >
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
            )}
            {visibleColumns.map((column) => {
              const isSortable = column.sortable !== false;
              const isSorted = sortColumn === column.id;
              return (
                <TableCell
                  key={column.id}
                  className="table-header-cell"
                  onClick={
                    isSortable ? () => handleSortToggle(column.id) : undefined
                  }
                  sx={{
                    cursor: isSortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {column.label}
                    </Typography>
                    {isSorted &&
                      (sortDirection === "asc" ? (
                        <ArrowUpwardIcon
                          sx={{ fontSize: "16px" }}
                          color="primary"
                        />
                      ) : (
                        <ArrowDownwardIcon
                          sx={{ fontSize: "16px" }}
                          color="primary"
                        />
                      ))}
                    {activeFilters.includes(column.id) && (
                      <FilterListIcon
                        fontSize="small"
                        color="primary"
                        sx={{ fontSize: "14px" }}
                      />
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
          {/* Filter inputs row */}
          {columnFilterable && (
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={`filter-${column.id}`}
                  className="table-header-filter-cell"
                  sx={{ padding: "4px 8px" }}
                >
                  {renderColumnFilter(column)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                align="center"
              >
                <Box py={4}>
                  <Typography>{t("common:loading.loading")}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : sortedData.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                align="center"
              >
                <Box py={4}>
                  <Typography color="text.secondary">
                    {searchQuery || activeFilters.length > 0
                      ? t("common:empty.noResultsFound")
                      : t("common:table.noDataAvailable")}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((row) => {
              const rowProps = getRowProps ? getRowProps(row) : {};
              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleRowClickInternal(row)}
                  className="table-row"
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    ...(rowProps.sx || {}),
                  }}
                  {...rowProps}
                >
                  {selectable && (
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedRows.has(row.id)}
                        onChange={(e) => handleRowSelect(row.id, e)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id}>
                      {renderCellValue(column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Mobile card view
  const renderMobileView = () => (
    <Box className="table-view-mobile">
      {loading ? (
        <Box py={4} textAlign="center">
          <Typography>{t("common:loading.loading")}</Typography>
        </Box>
      ) : sortedData.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            {searchQuery || activeFilters.length > 0
              ? t("common:empty.noResultsFound")
              : t("common:table.noDataAvailable")}
          </Typography>
        </Box>
      ) : (
        sortedData.map((row) => {
          const rowProps = getRowProps ? getRowProps(row) : {};
          return (
            <Card
              key={row.id}
              className="table-view-card"
              onClick={() => handleRowClickInternal(row)}
              sx={{
                cursor: onRowClick ? "pointer" : "default",
                mb: 2,
                ...(rowProps.sx || {}),
              }}
              {...rowProps}
            >
              <CardContent>
                {selectable && (
                  <Box mb={1} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRows.has(row.id)}
                      onChange={(e) => handleRowSelect(row.id, e)}
                      size="small"
                    />
                  </Box>
                )}
                {visibleColumns.map((column) => (
                  <Box key={column.id} mb={1.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {column.label}
                    </Typography>
                    <Typography variant="body2">
                      {renderCellValue(column, row)}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );

  return (
    <Box className="table-view">
      {/* Toolbar */}
      <Box className="table-view-toolbar" mb={2}>
        {searchable && (
          <TextField
            placeholder={t("common:actions.search") + "..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            className="table-view-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        )}
        <Box className="table-view-actions">
          {columnFilterable && activeFilters.length > 0 && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearAllFilters}
              sx={{ mr: 1 }}
            >
              {t("common:table.clearFilters")}
            </Button>
          )}
          <IconButton
            size="small"
            onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
            title={t("common:table.columnVisibility")}
          >
            <ViewColumnIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">
            {t("common:table.columnVisibility")}
          </Typography>
        </MenuItem>
        {columns.map((column) => (
          <MenuItem
            key={column.id}
            onClick={() => handleColumnVisibilityToggle(column.id)}
            dense
          >
            <Checkbox
              checked={columnVisibility[column.id] !== false}
              size="small"
              sx={{ mr: 1 }}
            />
            <Typography variant="body2">{column.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Table or Card View */}
      {isMobile ? renderMobileView() : renderDesktopView()}
    </Box>
  );
};

export default TableView;
